import React from 'react';
import { cn } from '../../lib/utils';
import { CircleDashed } from 'lucide-react';

interface LoaderProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const Loader: React.FC<LoaderProps> = ({ size = 'md', className }) => {
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
  };

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className={cn('relative', sizeClasses[size])}>
        {/* Circular design with elegant animation */}
        <div className="absolute inset-0 rounded-full border border-bytesplash-gray-200/30">
          {/* Circular grid pattern */}
          <div className="absolute inset-0 rounded-full">
            <div className="absolute inset-0 rounded-full border border-bytesplash-gray-200/20"></div>
            <div className="absolute inset-[15%] rounded-full border border-bytesplash-gray-200/20"></div>
            <div className="absolute inset-[30%] rounded-full border border-bytesplash-gray-200/20"></div>
            <div className="absolute inset-[45%] rounded-full border border-bytesplash-gray-200/20"></div>
          </div>
          
          {/* Animated circle segments */}
          <div className="absolute inset-0 rounded-full">
            <div className="absolute top-0 left-0 w-full h-full rounded-full">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle 
                  cx="50" cy="50" r="45" 
                  fill="none" 
                  stroke="rgba(0,113,189,0.3)" 
                  strokeWidth="2"
                  strokeDasharray="30 10" 
                  className="animate-[spin-slow_10s_linear_infinite]"
                />
                <circle 
                  cx="50" cy="50" r="38" 
                  fill="none" 
                  stroke="rgba(0,113,189,0.5)" 
                  strokeWidth="1.5"
                  strokeDasharray="60 15" 
                  className="animate-[spin-slow_8s_linear_infinite_reverse]"
                />
                <circle 
                  cx="50" cy="50" r="32" 
                  fill="none" 
                  stroke="rgba(0,113,189,0.7)" 
                  strokeWidth="1"
                  strokeDasharray="20 10" 
                  className="animate-[spin-slow_6s_linear_infinite]"
                />
              </svg>
            </div>
          </div>

          {/* Animated gradient overlay */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-bytesplash-primary/20 to-transparent animate-[shimmer_2s_infinite_linear]"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bytesplash-primary/20 to-transparent animate-[shimmer_2s_infinite_linear_0.5s]" style={{ animationDelay: '0.5s' }}></div>
          </div>
          
          {/* Central circle with pulse */}
          <div className="absolute inset-[40%] rounded-full bg-bytesplash-primary/10 backdrop-blur-sm animate-pulse"></div>
          <div className="absolute inset-[45%] rounded-full bg-bytesplash-primary/30"></div>
        </div>
        
        {/* Central icon */}
        <div className="absolute inset-0 flex items-center justify-center text-bytesplash-primary/80">
          <CircleDashed className="w-1/3 h-1/3 animate-[spin-slow_3s_linear_infinite]" />
        </div>
      </div>
    </div>
  );
};

export const PageLoader: React.FC = () => {
  return (
    <div className="fixed inset-0 bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm z-50 flex items-center justify-center">
      <Loader size="lg" />
    </div>
  );
};
